import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  OrbitControls,
  Environment,
  ContactShadows,
  useGLTF,
  Html,
  Lightformer,
  RandomizedLight,
  Stage,
} from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import Iconify from './Iconify';
import { useTranslation } from 'react-i18next';
const AnnotationLine = ({ start, end, isVisible }) => {
  const startPoint = new THREE.Vector3(...start);
  const endPoint = new THREE.Vector3(...end);
  endPoint.y -= 0.0;

  if (!isVisible) return null;

  return (
    <group>
      <line>
        <bufferGeometry>
          <float32BufferAttribute
            attach="attributes-position"
            args={[
              new Float32Array([
                startPoint.x,
                startPoint.y,
                startPoint.z,
                endPoint.x,
                endPoint.y,
                endPoint.z,
              ]),
              3,
            ]}
          />
        </bufferGeometry>
        <lineBasicMaterial color="white" transparent opacity={0.6} />
      </line>
      <mesh position={startPoint}>
        <circleGeometry args={[0.015, 32]} />
        <meshBasicMaterial color="white" transparent opacity={0.6} />
      </mesh>
    </group>
  );
};

const Annotation = ({ point, text, type, icon, isVisible }) => {
  if (!isVisible) return null;

  return (
    <Html position={point} center>
      <div
        className="flex flex-col gap-2 text-white select-none font-inter"
        style={{
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: 'none',
        }}
      >
        <div className="flex flex-shrink-0 uppercase min-w-[20ch] text-white/50 text-[10px]/[16px]">
          {type}
        </div>
        <div
          className="flex items-center gap-3 py-1 pl-2 border-l"
          style={{
            background:
              'linear-gradient(90deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0) 100%)',
          }}
        >
          <Iconify icon={icon} className="flex-shrink-0" />
          <p className="flex-shrink-0 text-[14px]/[22px]">{text}</p>
        </div>
      </div>
    </Html>
  );
};

const MODEL_Z_OFFSET = 0.3;
const CAMERA_Z_OFFSET = 2;

export default function EnhancedExperience({ modelUrl, isModelOpen, showDetails }) {
  const { t } = useTranslation();
  const racket = useGLTF(modelUrl);
  const racketRef = useRef();
  const lightsRef = useRef();
  const { camera } = useThree();
  const initialRotation = useRef({ x: 0, y: 0, z: 0 });

  const [annotations] = useState([
    {
      point: [-1.5, 0.2, MODEL_Z_OFFSET],
      type: t('weight'),
      text: '360g ± 10g',
      modelPoint: [0, 0, MODEL_Z_OFFSET],
      icon: 'streamline:justice-scale-1',
    },
    {
      point: [-1.5, 1.2, MODEL_Z_OFFSET],
      type: t('eva'),
      text: '15°',
      modelPoint: [-0.75, 1, MODEL_Z_OFFSET],
      icon: 'streamline:composition-vertical',
    },
    {
      point: [1.8, 1.2, MODEL_Z_OFFSET],
      type: t('face'),
      text: 'Twill Black 18K',
      modelPoint: [0.64, 1, MODEL_Z_OFFSET],
      icon: 'streamline:dna',
    },
    {
      point: [2.8, 0.1, MODEL_Z_OFFSET],
      type: t('faceFinish'),
      text: 'Matte',
      modelPoint: [0, 0.2, MODEL_Z_OFFSET],
      icon: 'streamline:zodiac-4',
    },
    {
      point: [1.2, -0.8, MODEL_Z_OFFSET],
      type: t('frame'),
      text: 'Carbon Fiber',
      modelPoint: [0, -0.5, MODEL_Z_OFFSET],
      icon: 'streamline:industry-innovation-and-infrastructure',
    },
    {
      point: [-1, -1, MODEL_Z_OFFSET],
      type: t('cup'),
      text: t('gyroSensor'),
      modelPoint: [0.01, -1.35, MODEL_Z_OFFSET],
      icon: 'streamline:dna',
    },
  ]);

  // Clean up reset timer on unmount
  useEffect(() => {
    return () => {
      if (resetTimer.current) {
        clearTimeout(resetTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    if (racket.scene) {
      const box = new THREE.Box3().setFromObject(racket.scene);
      const center = box.getCenter(new THREE.Vector3());
      racket.scene.position.sub(center);

      initialRotation.current = {
        x: racketRef.current.rotation.x,
        y: racketRef.current.rotation.y,
        z: racketRef.current.rotation.z,
      };

      racket.scene.traverse((child) => {
        if (child.isMesh) {
          child.material.transparent = true;
          child.material.opacity = 1;
        }
      });
    }
  }, [racket]);

  // Add state for tracking reset animation
  const [isResetting, setIsResetting] = useState(false);
  const resetTimer = useRef(null);
  const lastRotation = useRef(0);

  // Handle reset animation
  const handleReset = useCallback(() => {
    setIsResetting(true);
    lastRotation.current = racketRef.current.rotation.y;

    if (resetTimer.current) clearTimeout(resetTimer.current);

    resetTimer.current = setTimeout(() => {
      setIsResetting(false);
    }, 0);
  }, []);

  // Update useEffect to trigger reset animation
  useEffect(() => {
    if (!isModelOpen || !showDetails) {
      handleReset();
    }
  }, [isModelOpen, showDetails, handleReset]);

  useFrame((_state, delta) => {
    const easing = THREE.MathUtils.damp(0, 1, 2.5, delta);

    if (!isModelOpen || isResetting) {
      // Smooth transition to initial position
      camera.position.x = THREE.MathUtils.lerp(camera.position.x, 0, easing);
      camera.position.y = THREE.MathUtils.lerp(camera.position.y, 0, easing);
      camera.position.z = THREE.MathUtils.lerp(
        camera.position.z,
        CAMERA_Z_OFFSET,
        easing,
      );

      // Get nearest rotation point within one half rotation
      // const currentY = lastRotation.current;
      // const halfRotation = Math.PI;
      // const normalizedY = ((currentY % halfRotation) + halfRotation) % halfRotation;
      // const nearestY = normalizedY > halfRotation / 2 ? halfRotation : 0;

      // Smooth rotation reset to nearest point
      if (racketRef.current) {
        racketRef.current.rotation.x = THREE.MathUtils.lerp(
          racketRef.current.rotation.x,
          initialRotation.current.x,
          easing,
        );
        racketRef.current.rotation.y = THREE.MathUtils.lerp(
          racketRef.current.rotation.y,
          0.3,
          easing,
        );
        racketRef.current.rotation.z = THREE.MathUtils.lerp(
          racketRef.current.rotation.z,
          initialRotation.current.z,
          easing,
        );
      }

      return;
    }

    if (showDetails) {
      // Angled view for better annotation visibility
      camera.position.x = THREE.MathUtils.lerp(camera.position.x, 0, easing);
      camera.position.y = THREE.MathUtils.lerp(camera.position.y, 0, easing);
      camera.position.z = THREE.MathUtils.lerp(
        camera.position.z,
        CAMERA_Z_OFFSET,
        easing,
      );

      // Rotate racket for optimal viewing angle
      racketRef.current.rotation.x = THREE.MathUtils.lerp(
        racketRef.current.rotation.x,
        0,
        easing,
      );
      racketRef.current.rotation.y = THREE.MathUtils.lerp(
        racketRef.current.rotation.y,
        0.3,
        easing,
      );
      lastRotation.current = racketRef.current.rotation.y;

      racketRef.current.rotation.z = THREE.MathUtils.lerp(
        racketRef.current.rotation.z,
        0,
        easing,
      );
    }

    camera.lookAt(0, 0, 0);
  });

  return (
    <>
      {/* <Environment preset="studio" environmentIntensity={1} near={0.1}>

      </Environment> */}

      <OrbitControls
        enableZoom={isModelOpen && !showDetails}
        enablePan={isModelOpen && !showDetails}
        enableRotate={isModelOpen && !showDetails}
        enabled={!showDetails}
        autoRotate={isModelOpen && !showDetails}
        autoRotateSpeed={2.4}
      />

      <group ref={racketRef}>
        <group ref={lightsRef}>
          {/* <pointLight position={[4, 4, 4]} intensity={1}  /> */}

          {/* Key lighting from front-right */}
          <spotLight
            position={[4, 4, 4]}
            angle={0.4}
            penumbra={1}
            intensity={3}
            castShadow
          />

          {/* Fill light from left */}
          <spotLight
            position={[-4, 2, 0.3]}
            angle={0.5}
            penumbra={1}
            intensity={5}
            color="#b1e1ff"
          />

          {/* Rim light from behind */}
          <spotLight
            position={[0, 3, -1]}
            angle={0.5}
            penumbra={1}
            intensity={2}
            color="#ffffff"
          />

          {/* Additional front light for better visibility */}
          <spotLight
            position={[0, 0, 2]}
            angle={0.6}
            penumbra={1}
            intensity={1.5}
            color="#ffffff"
          />
        </group>

        <Stage intensity={1} environment="studio" center={[]}>
          <primitive object={racket.scene} />
        </Stage>

        {annotations.map((annotation, index) => (
          <group key={index}>
            <Annotation
              point={annotation.point}
              text={annotation.text}
              type={annotation.type}
              icon={annotation.icon}
              isVisible={showDetails && isModelOpen}
            />
            <AnnotationLine
              start={annotation.modelPoint}
              end={annotation.point}
              isVisible={showDetails && isModelOpen}
            />
          </group>
        ))}
      </group>

      <ambientLight intensity={0.8} />
      <ContactShadows
        position={[0, -1, 0]}
        opacity={0.4}
        scale={10}
        blur={2.5}
        far={4}
        color="#000000"
      />
    </>
  );
}
